/* ==========================================================================
   Yokabag — couche « Apple Design » (maquette)
   - Tiroirs glissables : suivi 1:1, interruptibles, passage de vitesse, projection d'élan, résistance élastique.
   - Apparitions déclenchées avant l'entrée à l'écran.
   Chargé après global.js ; ne modifie aucun composant existant, il les complète.
   ========================================================================== */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // iOS Safari n'applique :active que si un écouteur tactile existe.
  document.addEventListener('touchstart', () => {}, { passive: true });

  /* ---------------------------------------------------------------- Physique */
  // Distance parcourue par un élan avant de s'arrêter (décélération du défilement iOS).
  const project = (velocity, rate = 0.998) => (velocity / 1000) * rate / (1 - rate);

  // Au-delà d'une limite, l'élément suit de moins en moins le doigt.
  const rubberband = (overshoot, dimension, c = 0.55) =>
    (overshoot * dimension * c) / (dimension + c * Math.abs(overshoot));

  // Ressort amorti critique (amortissement 1.0) : part de la position et de la vitesse actuelles.
  const spring = ({ from, to, velocity = 0, response = 0.4, onUpdate, onComplete }) => {
    const w = (2 * Math.PI) / response;
    const a = from - to;
    const b = velocity + w * a;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = (now - start) / 1000;
      const decay = Math.exp(-w * t);
      const x = to + (a + b * t) * decay;
      const v = (b - w * (a + b * t)) * decay;
      if (Math.abs(x - to) < 0.5 && Math.abs(v) < 10) { onUpdate(to); onComplete?.(); return; }
      onUpdate(x);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return { stop: () => cancelAnimationFrame(raf) };
  };

  // Valeur affichée à l'écran, même au milieu d'une transition CSS.
  const presentationX = (el) => {
    const m = getComputedStyle(el).transform;
    return m && m !== 'none' ? new DOMMatrixReadOnly(m).m41 : 0;
  };

  /* ---------------------------------------------------------------- Tiroirs glissables */
  const SLOP = 10; // hystérésis avant de décider la direction du geste

  const scrollsSideways = (el, stop) => {
    for (let n = el; n && n !== stop; n = n.parentElement) {
      if (n.scrollWidth > n.clientWidth + 2 && /auto|scroll/.test(getComputedStyle(n).overflowX)) return true;
    }
    return false;
  };

  const enhanceDrawer = (drawer) => {
    if (drawer._fluid || drawer.classList.contains('drawer--top')) return;
    const panel = drawer.querySelector(':scope > .drawer__panel');
    const overlay = drawer.querySelector(':scope > .drawer__overlay');
    if (!panel) return;
    drawer._fluid = true;

    const dir = drawer.classList.contains('drawer--left') ? -1 : 1; // sens de fermeture
    let gesture = null;
    let anim = null;

    const width = () => panel.getBoundingClientRect().width || window.innerWidth;
    const render = (x) => {
      panel.style.transform = `translateX(${x}px)`;
      if (overlay) overlay.style.opacity = String(Math.max(0, 1 - Math.abs(x) / width()));
    };
    const clear = () => {
      anim?.stop(); anim = null;
      // classList.remove réécrit l'attribut même si la classe est absente : on ne touche à class que si nécessaire,
      // sinon l'observateur ci-dessous se relance à l'infini et fige la page.
      if (drawer.classList.contains('is-dragging')) drawer.classList.remove('is-dragging');
      panel.style.transform = '';
      if (overlay) overlay.style.opacity = '';
    };

    // Fermeture par la croix ou Échap pendant une animation pilotée en JS : on rend la main au CSS.
    // On ne réagit qu'au passage ouvert → fermé.
    let wasOpen = drawer.classList.contains('is-open');
    new MutationObserver(() => {
      const isOpen = drawer.classList.contains('is-open');
      if (wasOpen && !isOpen && !gesture) clear();
      wasOpen = isOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    panel.addEventListener('pointerdown', (e) => {
      if (!drawer.classList.contains('is-open') || e.button !== 0) return;
      if (e.target.closest('input, textarea, select, [contenteditable], [data-no-drag]')) return;
      gesture = {
        id: e.pointerId, x0: e.clientX, y0: e.clientY,
        dragging: false, sideways: scrollsSideways(e.target, panel),
        samples: [{ x: e.clientX, t: e.timeStamp }]
      };
      // Interruption : si le tiroir est en mouvement, on le rattrape là où il est.
      if (anim) { anim.stop(); anim = null; gesture.base = presentationX(panel); startDrag(e); }
    });

    const startDrag = (e) => {
      gesture.dragging = true;
      if (gesture.base === undefined) gesture.base = presentationX(panel);
      gesture.x0 = e.clientX; // le point de saisie est respecté : pas de saut
      drawer.classList.add('is-dragging');
      try { panel.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      render(gesture.base);
    };

    panel.addEventListener('pointermove', (e) => {
      if (!gesture || e.pointerId !== gesture.id) return;
      const dx = e.clientX - gesture.x0;
      const dy = e.clientY - gesture.y0;
      if (!gesture.dragging) {
        if (gesture.sideways || Math.abs(dy) > SLOP) { gesture = null; return; } // défilement vertical : on laisse faire
        if (Math.abs(dx) < SLOP) return;
        startDrag(e);
        return;
      }
      gesture.samples.push({ x: e.clientX, t: e.timeStamp });
      if (gesture.samples.length > 6) gesture.samples.shift();
      let x = gesture.base + (e.clientX - gesture.x0);
      // Tirer vers l'intérieur : résistance progressive au lieu d'un arrêt sec.
      if (x * dir < 0) x = rubberband(x, width());
      render(x);
    });

    const release = (e) => {
      if (!gesture || e.pointerId !== gesture.id) return;
      const g = gesture;
      gesture = null;
      if (!g.dragging) return;
      drawer._justDragged = true;
      setTimeout(() => { drawer._justDragged = false; }, 0);
      // Vitesse sur les ~100 dernières ms, en px/s. Doigt immobile avant de relâcher : vitesse nulle.
      g.samples.push({ x: e.clientX, t: e.timeStamp });
      const recent = g.samples.filter((p) => e.timeStamp - p.t < 100);
      let velocity = 0;
      if (recent.length >= 2) {
        const a = recent[0];
        const b = recent[recent.length - 1];
        velocity = ((b.x - a.x) / Math.max(1, b.t - a.t)) * 1000;
      }
      const x = presentationX(panel);
      const w = width();
      // On décide sur la position projetée, pas sur la position au relâchement.
      const projected = x + project(velocity);
      const dismiss = projected * dir > w / 2;
      const target = dismiss ? dir * w : 0;

      const finish = () => {
        anim = null;
        if (dismiss) {
          // Déjà hors écran : on ferme sans rejouer la transition CSS.
          panel.style.transition = 'none';
          if (overlay) overlay.style.transition = 'none';
          drawer.close();
          clear();
          void panel.offsetWidth;
          panel.style.transition = '';
          if (overlay) overlay.style.transition = '';
        } else {
          clear();
        }
      };
      if (reduceMotion.matches) { render(target); finish(); return; }
      anim = spring({ from: x, to: target, velocity, response: 0.4, onUpdate: render, onComplete: finish });
    };
    panel.addEventListener('pointerup', release);
    panel.addEventListener('pointercancel', release);

    // Après un glisser, le clic de fin ne doit pas déclencher un lien ou un bouton.
    panel.addEventListener('click', (e) => { if (drawer._justDragged) { e.preventDefault(); e.stopPropagation(); } }, true);
  };

  /* ---------------------------------------------------------------- Apparitions anticipées */
  const reveal = (root = document) => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px 15% 0px' }); // avant l'entrée à l'écran, pas 8 % après
    root.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => io.observe(el));
  };

  const init = (root = document) => {
    root.querySelectorAll('.drawer').forEach(enhanceDrawer);
    reveal(root);
  };
  /* ---------------------------------------------------------------- Diode « En stock » */
  // À chaque changement de variante, l'onde se rejoue pour confirmer la disponibilité.
  // Si la section a été re-rendue, la nouvelle diode joue déjà son onde : on ne la coupe pas.
  document.addEventListener('variant:change', (e) => {
    const scope = e.target.closest('[data-product-scope]') || document;
    setTimeout(() => {
      scope.querySelectorAll('.stock--in .stock__dot').forEach((dot) => {
        if (!dot.dataset.pinged) { dot.dataset.pinged = '1'; return; }
        dot.classList.add('is-replay');
        void dot.offsetWidth;
        dot.classList.remove('is-replay');
      });
    }, 450);
  });
  const markDots = () => document.querySelectorAll('.stock--in .stock__dot').forEach((d) => { d.dataset.pinged = '1'; });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { init(); markDots(); });
  else { init(); markDots(); }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
